import type { ScheduledStem } from './types';
import type { TransportClock } from '../../../scheduler/clock';
import { mediaTimeAt } from '../../../scheduler/clock';
import { ev, nextId } from './debug';
import { computeSpeedMapping } from '../speedMapping';
import { L } from '../../../utils/logger';
import { ctxOk, safeConnectCtx } from './ctxUtil';
import { fullyDisposeRbNode } from './worklets';

export function makeScheduled(
  ctx: AudioContext,
  buffers: Record<string, AudioBuffer>,
  roles: string[],
  masterGain: GainNode,
): Record<string, ScheduledStem> {
  const map: Record<string, ScheduledStem> = {};

  for (const role of roles) {
    const buffer = buffers[role];
    if (!buffer) continue;

    // Core per-stem nodes
    const gain = ctx.createGain();
    const delay = ctx.createDelay(0.1);
    const analyser = ctx.createAnalyser();

    try { delay.delayTime.value = 0; } catch {}
    try { gain.gain.value = 1; } catch {}
  try { analyser.smoothingTimeConstant = 0.17; } catch {}

    // Wire into master by default (direct path until a worklet is attached)
    const actx = ctx;
    if (ctxOk(actx)) {
      safeConnectCtx(actx, delay, analyser);
      // chain delay -> gain -> masterGain using guarded connects
      if (safeConnectCtx(actx, delay, gain)) {
        safeConnectCtx(actx, gain, masterGain);
      }
    }

    // Pre-provision worklet fields (to be filled by ensurePassthroughWorklet or ensureRubberBandWorklet)
    map[role] = {
      role,
      buffer,
      source: null,
      gain,
      delay,
      analyser,
      worklet: null,     // will hold { node } later
      useWorklet: false, // opt-in enabled by worklet attachers
    };
  }

  return map;
}

export function scheduleStartAll(
  ctx: AudioContext,
  stems: Record<string, ScheduledStem>,
  whenLookahead = 0.05,
  offsetSec = 0,
  rate = 1.0,
  perRoleOffset?: Record<string, number>,
  options?: {
    seamlessSwap?: boolean;
    fixedLatencySec?: number;
    clock?: TransportClock;
    // Optional looping (off by default). If enabled, we rely on WA's native looping
    // and we DO NOT pass duration to start(), nor do we modulo offsets ourselves.
    loop?: boolean;
    loopStart?: number;
    loopEnd?: number;
  },
) {
  const now = ctx.currentTime;
  const hidden = (() => { try { return typeof document !== 'undefined' && document.visibilityState !== 'visible'; } catch { return false; } })();
  const MIN_START = hidden ? 0.20 : 0.10; // slightly larger look-ahead when backgrounded
  const when = now + Math.max(MIN_START, whenLookahead);
  const baseLatency = Math.max(0, options?.fixedLatencySec ?? 0);
  const schedId = nextId('sched');
  L.info('media:sched', 'startAll', { schedId, roles: Object.keys(stems), rate, offsetSec, lookahead: (when - now).toFixed(3), baseLatency });
  ev('sched.begin', { schedId, when, now, offsetSec, rate, baseLatency, roles: Object.keys(stems), opts: { ...options, clock: options?.clock ? 'present' : 'none' } }, ctx);
  // Intentionally ignore any external clock for startOffset.
  // We schedule strictly from logicalStartRef (offsetSec) to avoid double counting.
  const bufDur = (r: ScheduledStem) => r.buffer.duration || 0;

  for (const [role, s] of Object.entries(stems)) {
    if (!s) continue;

    // Reset delay node to base latency (kept constant during this schedule)
    safe(`reset delay for ${role}`, () => {
      s.delay.delayTime.cancelScheduledValues(when - 0.05);
      s.delay.delayTime.setValueAtTime(baseLatency, when - 0.001);
    });

  // Stop existing source
  stopExistingSource(s, when, options?.seamlessSwap, schedId, role);

  // Create source
  const bs = createBufferSource(ctx, s, rate);
    ev('stem.prepare', {
      schedId, role,
      bufDur: s.buffer.duration,
      // Consider RB "present" only when node exists, opted-in, and has reported live
      useWorklet: !!(s.worklet?.node) && s.useWorklet === true && (s as any)?._rbLive === true,
      rateParam: rate,
      baseLatency
    }, ctx);

  // Optional loop configuration: prefer WA native loop over manual modulo
  // so we keep sample-accurate wrap and simpler scheduling semantics.
  if (options?.loop) configureLoop(bs, s.buffer.duration, options.loopStart, options.loopEnd);

    // Connect graph
    connectStem(s, bs);

  // TEMP A/B: disable runtime delay ramp to test stability at slow speeds
  // if (roleOff > 0) applyRoleOffset(s, roleOff, when, baseLatency);

  // Compute offset from the time audio will be heard to avoid subtle double-counting
    const totalDur = bufDur(s);
    const deviceLatency = Math.max(0, options?.fixedLatencySec ?? 0);
    const audibleAt = when + deviceLatency; // when it hits the ears
    const mediaAtAudible = options?.clock
      ? mediaTimeAt(options.clock, audibleAt)
      : offsetSec;
    const baseStart = totalDur > 0 ? ((mediaAtAudible % totalDur) + totalDur) % totalDur : 0;
    const eps = 0.001;
    let startOffset = baseStart;
    if (options?.loop && totalDur > 0) {
      const ls = Math.max(0, options.loopStart ?? 0);
      const le = Math.min(totalDur, Math.max(ls + eps, options.loopEnd ?? totalDur));
      const span = Math.max(eps, le - ls);
      // Map baseStart into the loop window [ls, le)
      startOffset = ls + ((((baseStart - ls) % span) + span) % span);
      // Keep a tiny epsilon away from exact end
      if (startOffset > le - eps) startOffset = le - eps;
    }
  // Final guard: never schedule in the past (helps avoid first-slice drop on some devices)
  const safeWhen = Math.max(now + 0.02, when);
    ev('stem.start', {
      schedId, role,
      when: safeWhen,
      offset: startOffset,
      bufDur: totalDur,
      loop: !!bs.loop,
      loopStart: (bs as any).loopStart ?? 0,
      loopEnd: (bs as any).loopEnd ?? totalDur,
      useWorklet: !!(s.worklet?.node) && s.useWorklet === true && (s as any)?._rbLive === true,
      rateParam: rate
    }, ctx);
  // Ensure we cleanup connection when a source naturally ends
  try {
    (bs as any).onended = () => {
      L.debug('media:sched', 'source.ended', { schedId, role: s.role });
      safe('onended.disconnect', () => {
        try { bs.disconnect(); } catch {}
      });
    };
  } catch {}

  startSource(bs, safeWhen, startOffset);
  // Smooth fade-in to avoid clicks; ramp to current gain over ~20ms
  try {
    const tgt = Number((s.gain.gain as any).value ?? 1);
    s.gain.gain.setValueAtTime(0, safeWhen);
    s.gain.gain.linearRampToValueAtTime(Math.max(0, tgt), safeWhen + 0.02);
  } catch {}

    s.source = bs;
    // RB-aware primary wiring at creation time to avoid any bypass frames
    try { s.source.disconnect(); } catch {}
    const hasRb = !!(s.worklet?.node);
    const sink: any = hasRb ? s.worklet!.node! : s.delay;
    if (safeConnectCtx(ctx, s.source as any, sink)) {
      if (hasRb) safeConnectCtx(ctx, s.worklet!.node!, s.delay);
    }
  }
  try { L.info('media:sched', 'end', { schedId, when }); } catch {}
  return when;
}

/* --- Helpers --- */

function stopExistingSource(s: ScheduledStem, when: number, seamless?: boolean, schedId?: string, role?: string) {
  safe("stopExistingSource", () => {
    if (s.source) {
  try { ev('stem.stop', { schedId, role, when, seamless: !!seamless }, s.source.context); } catch {}
      if (seamless) {
        try { s.source.stop(when); } catch { s.source.stop(); }
      } else {
        s.source.stop();
      }
      // Proactively disable loop and detach handlers to avoid lingering refs
      try { (s.source as any).loop = false; } catch {}
      try { (s.source as any).onended = null; } catch {}
      // Disconnect and drop buffer to cut the signal path immediately
      try { (s.source as any)?.disconnect?.(); } catch {}
      try { if ((s.source as any).buffer) (s.source as any).buffer = null; } catch {}
    }
    s.source = null;
  });
}

function createBufferSource(ctx: AudioContext, s: ScheduledStem, rate: number) {
  const bs = ctx.createBufferSource();
  bs.buffer = s.buffer;

  const ch = Math.max(1, s.buffer.numberOfChannels || 1);
  (bs as any).channelCountMode = 'explicit';
  (bs as any).channelInterpretation = 'speakers';
  (bs as any).channelCount = ch;

  // Decide WebAudio rate via shared policy mapping. Treat maintain-pitch as enabled
  // only when the worklet exists, is opted-in, and has reported 'live' (actively processing).
  const mp = !!(s.worklet?.node) && s.useWorklet === true && (s as any)?._rbLive === true;
  const map = computeSpeedMapping(rate, mp);
  bs.playbackRate.value = map.webAudioRate;
  try {
    ev('stem.source', {
      role: s.role,
      ch: Math.max(1, s.buffer.numberOfChannels || 1),
      PRvalue: (bs.playbackRate as any)?.value ?? map.webAudioRate,
      useWorklet: !!(s.worklet?.node) && s.useWorklet === true && (s as any)?._rbLive === true
    }, ctx);
  } catch {}
  return bs;
}

function configureLoop(
  bs: AudioBufferSourceNode,
  bufferDuration: number,
  loopStart?: number,
  loopEnd?: number,
) {
  const ls = Math.max(0, loopStart ?? 0);
  const le = Math.min(bufferDuration, Math.max(ls, loopEnd ?? bufferDuration));
  // Rely on WA engine for loop wrapping; do not pass duration to start().
  bs.loop = true;
  bs.loopStart = ls;
  bs.loopEnd = le;
}

function connectStem(s: ScheduledStem, bs: AudioBufferSourceNode) {
  const actx = bs?.context ?? null;
  if (!ctxOk(actx)) return;
  if (s.worklet?.node && s.useWorklet) {
    safe("connect via worklet", () => {
      if (safeConnectCtx(actx, bs, s.worklet!.node!)) {
        safeConnectCtx(actx, s.worklet!.node!, s.delay);
      }
    });
  } else {
    safe("connect direct", () => { safeConnectCtx(actx, bs, s.delay); });
  }
}

function startSource(bs: AudioBufferSourceNode, when: number, offsetSec: number) {
  const bufDur = bs.buffer?.duration ?? 0;
  const ctxNow = bs.context.currentTime;
  const safeWhen = Math.max(ctxNow + 0.02, when); // never schedule in the past
  const off = Math.max(0, Math.min(bufDur - 0.001, offsetSec)); // keep inside buffer
  safe("startSource", () => {
    // Let it run to buffer end
    bs.start(safeWhen, off);
    // Record debug metadata for resync/triage
    try { (bs as any)._startTime = safeWhen; (bs as any)._offset = off; } catch {}
  });
}

function safe<T>(label: string, fn: () => T) {
  try { return fn(); }
  catch (err) { try { L.warn('media:sched', 'safe.fail', { label, err: String(err) }); } catch {} }
}


export function stopAll(stems: Record<string, ScheduledStem>) {
  for (const role of Object.keys(stems)) {
    const s = stems[role];
    try { s.source?.stop(); } catch {}
    try { if (s.source && (s.source as any).loop) (s.source as any).loop = false; } catch {}
    try { if (s.source && (s.source as any).onended) (s.source as any).onended = null; } catch {}
    try { (s.source as any)?.disconnect?.(); } catch {}
    try { if (s.source && (s.source as any).buffer) (s.source as any).buffer = null; } catch {}
    s.source = null;
  }
}

export function disposeScheduled(stems: Record<string, ScheduledStem>) {
  for (const role of Object.keys(stems)) {
    const s = stems[role];
    try {
      // Hard cut: ramp gain to 0 and disconnect graph first to prevent any residual audio
      try {
        const t = (s.gain as any)?.context?.currentTime ?? 0;
        try { (s.gain.gain as any)?.cancelScheduledValues?.(t); } catch {}
        try { (s.gain.gain as any)?.setValueAtTime?.(0, t); } catch {}
      } catch {}
      try { s.gain.disconnect(); } catch {}
      try { s.delay.disconnect(); } catch {}
      try { s.analyser.disconnect(); } catch {}
      // Now stop and detach any active source and worklet for this stem
      try { disposeStem(s); } catch {}
    } catch {}
    // Also clear object references to expedite collection
    try { (s as any).delay = null; } catch {}
    try { (s as any).gain = null; } catch {}
    try { (s as any).analyser = null; } catch {}
  }
}

/**
 * Deterministic teardown of a single ScheduledStem:
 * - Ask the processor to dispose, clear handlers, close port, and disconnect node
 * - Stop and disconnect source, clear buffer reference
 * - Null local strong references so GC can reclaim WASM heaps and buffers
 */
export async function disposeStem(s: ScheduledStem) {
  if (!s) return;
  // Worklet/processor teardown
  try { fullyDisposeRbNode(s.worklet?.node, s); } catch {}

  // Source teardown
  try { (s.source as any)?.stop?.(0); } catch {}
  try { if (s.source && (s.source as any).loop) (s.source as any).loop = false; } catch {}
  try { if (s.source && (s.source as any).onended) (s.source as any).onended = null; } catch {}
  try { (s.source as any)?.disconnect?.(); } catch {}
  try { if ((s.source as any)?.buffer != null) (s.source as any).buffer = null; } catch {}

  // Drop strong refs
  try { s.worklet = null as any; } catch {}
  try { s.source = null as any; } catch {}
  try { (s as any).buffer = undefined; } catch {}
}

export function setStemVolume(stems: Record<string, ScheduledStem>, role: string, v: number) {
  const s = stems[role]; if (!s) return;
  try { s.gain.gain.value = Math.max(0, v); } catch {}
}

export function microAlignAhead(
  ctx: AudioContext,
  stems: Record<string, ScheduledStem>,
  role: string,
  seconds: number,
) {
  const s = stems[role]; if (!s) return;
  const now = ctx.currentTime;
  const add = Math.min(0.03, Math.max(0, seconds));
  try {
    s.delay.delayTime.cancelScheduledValues(now);
    s.delay.delayTime.setValueAtTime(add, now);
    s.delay.delayTime.linearRampToValueAtTime(0, now + 0.25);
  } catch {}
}

/**
 * Restart a single role at the given media offset, using AudioContext time with a small lead.
 * Applies a short fade-in to the stem gain to avoid clicks.
 * Returns the scheduled start time (t0) used.
 */
export function restartRole(
  ctx: AudioContext,
  stems: Record<string, ScheduledStem>,
  role: string,
  offsetSec: number,
  rate = 1.0,
  options?: { seamlessSwap?: boolean }
) {
  const s = stems[role];
  if (!s) return undefined as unknown as number;
  const now = ctx.currentTime;
  const lead = 0.05;
  const t0 = now + lead;
  // Stop existing
  stopExistingSource(s, t0, options?.seamlessSwap, undefined, role);
  // New source
  const bs = createBufferSource(ctx, s, rate);
  connectStem(s, bs);
  // Schedule start
  startSource(bs, t0, offsetSec);
  // Fade in to current target gain
  try {
    const tgt = Number((s.gain.gain as any).value ?? 1);
    s.gain.gain.setValueAtTime(0, t0);
    s.gain.gain.linearRampToValueAtTime(Math.max(0, tgt), t0 + 0.02);
  } catch {}
  s.source = bs;
  // RB-aware primary wiring at creation time to avoid bypass on restarts
  try { s.source.disconnect(); } catch {}
  const hasRb = !!(s.worklet?.node);
  const sink: any = hasRb ? s.worklet!.node! : s.delay;
  if (safeConnectCtx(ctx, s.source as any, sink)) {
    if (hasRb) safeConnectCtx(ctx, s.worklet!.node!, s.delay);
  }
  return t0;
}
